import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import queryString from "query-string";
import ReactPaginate from "react-paginate";
import moment from "moment";
import _ from "lodash";

import Spinner from "../../components/generics/Spinner";
import Filter from "../../components/generics/Filter";
import ListComparisonHeader from "../../components/generics/ListComparisonHeader";
import { comparePriceLists } from "../../store/priceLists/api";

import { fetchBrands, fetchBrandsSuccess, fetchBrandsFailure } from "../../store/inventories/actions";

import { STARTING_PAGE, DEBOUNCE_SEARCH } from "../../constants/constants";

import Alert from "../../utils/Alert";

import {
  comparePricesLists as storePriceListsComparison,
  comparePricesListsSuccess,
  comparePricesListsFailure,
} from "../../store/priceLists/actions";

import PricesComparison from "../../components/priceLists/PricesComparison";
import PricesComparisonDetail from "../../components/priceLists/PricesComparisonDetail";
import { withRouter } from "utils/WithRouter";

const PriceListsComparison = ({ history, fetchBrands, location, priceListsComparison, comparePricesListsPaginated, brandsList }) => {
  const [search, setSearch] = useState(queryString.parse(location.search).search);
  const [page, setPage] = useState(1);
  const [brand, setBrand] = useState(undefined);
  const [businessType, setBusinessType] = useState(undefined);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(undefined);
  const [csvIsLoading, setCsvIsLoading] = useState(false);
  const [pdfIsLoading, setPdfIsLoading] = useState(false);
  const alertRef = useRef(null);

  useEffect(() => {
    const params = queryString.parse(location.search);
    setPage(params.page ? params.page : STARTING_PAGE);
    setBrand(params.brand ? params.brand : undefined);
    setBusinessType(params.businessType ? params.businessType : undefined);
    setSearch(params.vendor ? params.vendor : undefined);
    fetchBrands(true, params.ids);
  }, []);

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------
  const handlePageRequest = (data) => {
    let newPage = data.selected + 1;
    if (newPage !== page) {
      setPage(newPage);
    }
  };

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------

  useEffect(() => {
    debounceSearch();
    return debounceSearch.cancel;
  }, [brand, page, businessType, search]);

  const onSearchChange = (event) => {
    const search = event.target.value ? event.target.value : "";
    setPage(STARTING_PAGE);
    setSearch(search);
  };

  const onBrandChange = (event) => {
    const brand = event.target.value && event.target.value > -1 ? event.target.value : undefined;
    setPage(STARTING_PAGE);
    setBrand(brand);
  };

  const onBusinessTypeChange = (event) => {
    const businessType = event.target.value && event.target.value != "-1" ? event.target.value : undefined;
    setPage(STARTING_PAGE);
    setBusinessType(businessType);
  };

  const filterList = () => {
    let params = {};
    params.page = page;
    const searchParams = new URLSearchParams(location.search);
    params.ids = searchParams.get("ids");
    if (brand) {
      params.brand = brand;
    }
    if (businessType) {
      params.businessType = businessType;
    }
    if (search) {
      params.search = search;
    }
    history.replace({
      search: queryString.stringify(params),
    });
    comparePricesListsPaginated(searchParams.get("ids"), page, brand, businessType, search, "paginated");
  };

  const debounceSearch = _.debounce(filterList, DEBOUNCE_SEARCH);

  // ---------------------------------------------------------------------------
  // Exports
  // ---------------------------------------------------------------------------

  const loadCsv = () => {
    const firstPrice = priceListsComparison.comparison.results.length && priceListsComparison.comparison.results[0];
    const vendorNames = firstPrice.offer_item_prices && Object.keys(firstPrice.offer_item_prices);
    const fileName = `price_comparison_${vendorNames.join("_")}_${moment().format("MM-DD-YYYY")}.csv`;
    const params = queryString.parse(location.search);
    const comparisonType = "csv";
    setCsvIsLoading(true);
    comparePriceLists(params.ids, null, brand, businessType, search, comparisonType).then((r) => {
      const blob = new Blob([r.data], { type: "text/csv" });
      let link = document.createElement("a");
      document.body.appendChild(link);
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", fileName);
      link.click();
      document.body.removeChild(link);
      setCsvIsLoading(false);
    });
  };

  const printPdf = () => {
    setPdfIsLoading(true);
    const firstPrice = priceListsComparison.comparison.results.length && priceListsComparison.comparison.results[0];
    const vendorNames = firstPrice.offer_item_prices && Object.keys(firstPrice.offer_item_prices);
    const fileName = `price_comparison_${vendorNames.join("_")}_${moment().format("MM-DD-YYYY")}.pdf`;
    const params = queryString.parse(location.search);
    const comparisonType = "pdf";
    comparePriceLists(params.ids, null, brand, businessType, search, comparisonType)
      .then((r) => {
        let blob = new Blob([r.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        setPdfIsLoading(false);
      })
      .catch((error) => {
        if (error.response) alertRef.current.sendAlert(error.response.data);
        setPdfIsLoading(false);
      });
  };

  const getVendorNames = (prices) => {
    const firstPrice = prices.length && prices[0];
    const vendorNames = firstPrice.offer_item_prices && Object.keys(firstPrice.offer_item_prices);
    return vendorNames && vendorNames.map((name) => <th>{name}</th>);
  };

  return (
    <div className="animated fadeIn">
      <Alert ref={alertRef} />
      <div className="card">
        <ListComparisonHeader
          dark
          showAction={false}
          title={"Price list"}
          iconClass="fa fa-file-text-o mr-05"
          actionTitle="Export CSV"
          csvLoading={csvIsLoading}
          fromFile={true}
          loadCsv={loadCsv}
          pdfLoading={pdfIsLoading}
          printPdf={printPdf}
        />
        <Filter
          searchLabel2="Search"
          searchPlaceholder2="Search by description, brand, item code or business type"
          searchVal2={search}
          onSearchChange2={onSearchChange}
          dropdownLabel2="Filter by brand"
          dropdownPlaceholder2="Select brand"
          dropdownVal2={brand}
          dropdownByElementId2
          dropdownOptions2={brandsList ? brandsList.brands.results : []}
          onDropdownChange2={onBrandChange}
          dropdownLabel="Filter by business type"
          dropdownPlaceholder="Select business type"
          dropdownVal={businessType}
          dropdownByElementId
          dropdownOptions={[
            { id: "business_type_mg", name: "MG" },
            { id: "business_type_restaurant", name: "Restaurant" },
            { id: "business_type_market", name: "Market" },
            { id: "business_type_pizzeria", name: "Pizzeria" },
          ]}
          onDropdownChange={onBusinessTypeChange}
          colClass="col-12 col-sm-3"
        />
      </div>
      {priceListsComparison.loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="card">
            <div className="card-block p-0" style={{ overflow: "hidden" }}>
              <div>
                <table className="table table-bordered datatable mb-0">
                  <thead>
                    <tr>
                      <th>Item</th>
                      {priceListsComparison.comparison && getVendorNames(priceListsComparison.comparison.results)}
                    </tr>
                  </thead>
                  {priceListsComparison.comparison && (
                    <PricesComparison pricesList={priceListsComparison.comparison.results} openPriceDetail={setIsDetailModalOpen} />
                  )}
                </table>
              </div>
              {priceListsComparison.pages > 1 && (
                <ReactPaginate
                  pageCount={priceListsComparison.pages}
                  pageRangeDisplayed={2}
                  marginPagesDisplayed={1}
                  previousLabel="<"
                  nextLabel=">"
                  onPageChange={handlePageRequest}
                  initialPage={page - 1}
                  disableInitialCallback={true}
                  containerClassName="pagination"
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  activeClassName="page-item active"
                  previousClassName="paginate_button previous"
                  nextClassName="paginate_button next"
                  disabledClassName="page-item disabled"
                  forcePage={page - 1}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {isDetailModalOpen && <PricesComparisonDetail price={isDetailModalOpen} closeModal={() => setIsDetailModalOpen(undefined)} />}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    priceListsComparison: state.priceLists.priceListsComparison,
    brandsList: state.inventories.brandsList,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    comparePricesListsPaginated: (ids, page, brand, businessType, search, comparisonType) => {
      dispatch(storePriceListsComparison(ids, page, brand, businessType, search, comparisonType)).payload.then(
        (response) => {
          dispatch(comparePricesListsSuccess(response.data));
        },
        (error) => {
          dispatch(comparePricesListsFailure(error));
        }
      );
    },
    fetchBrands: (includeVendorBrands, ids) => {
      dispatch(fetchBrands(includeVendorBrands, ids)).payload.then(
        (response) => {
          dispatch(fetchBrandsSuccess(response.data));
        },
        (error) => {
          dispatch(fetchBrandsFailure(error));
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PriceListsComparison));
